// Componenta principală a aplicației - orchestrator pentru rutare între pagini
import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { RouterProvider } from "react-router";

// Router configuration
import { createAppRouter } from './router';

import { logWithTimestamp } from './utils/dateHelpers';

// Import store Zustand pentru autentificare
import { useAuthStore } from './stores/authStore';
// Import pentru inițializarea categoriilor
import { CATEGORIES } from '@buzunarul/shared-constants/categories';
import { useCategoryStore } from './stores/categoryStore';
// Import pentru migrarea conturilor
import useAccountMigration from './hooks/useAccountMigration';
import { useGlobalDateStore } from './stores/globalDateStore';
// Import pentru inițializarea balanceStore
import { useBalanceStoreInitialization } from './hooks/useBalanceStoreInitialization';

// Components
import Spinner from './components/primitives/Spinner';
import { ToastContainer, ToastProvider } from './components/primitives/Toast';
import { OfflineIndicator } from './components/shared/OfflineIndicator';

/**
 * 🎯 CLEAN ORCHESTRATOR - App Component
 * 
 * Refactorizat pentru React Router v7
 */
export const App = () => {
  const { user, loading, checkUser, logout } = useAuthStore();

  // Migrarea conturilor din localStorage în Supabase
  useAccountMigration();

  // Inițializarea balanceStore cu recalcularea dailyBalances
  const balanceInitialization = useBalanceStoreInitialization();

  // Inițializarea categoriilor la nivel global
  const loadCategories = useCategoryStore(state => state.loadUserCategories);
  const mergeWithDefaults = useCategoryStore(state => state.mergeWithDefaults);

  // Porneşte ceasul global la montare
  const startClock = useGlobalDateStore(s => s.startClock);
  useEffect(() => {
    startClock();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Verificăm dacă utilizatorul este autentificat
  useEffect(() => {
    checkUser();
  }, [checkUser]);

  // Inițializăm categoriile după autentificare
  useEffect(() => {
    if (!user) return;

    logWithTimestamp('[App] Inițializare categorii globală');

    const initializeCategories = async () => {
      try {
        await loadCategories(user.id);

        const defaultCategories = Object.entries(CATEGORIES).map(([name, subcats]) => ({
          name,
          subcategories: Object.values(subcats)
            .flat()
            .map(subcatName => ({
              name: subcatName,
              isCustom: false,
            })),
          isCustom: false,
        }));

        mergeWithDefaults(defaultCategories);
        logWithTimestamp('[App] Categorii inițializate cu succes');
      } catch (error) {
        console.error('[App] Eroare la inițializarea categoriilor:', error);
      }
    };

    initializeCategories();
  }, [user?.id]);

  // Handle logout function
  const handleLogout = async () => {
    try {
      await logout();
      logWithTimestamp('User logged out via AppLayout');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // ✅ LOADING STATES
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  // ✅ BALANCE INITIALIZATION
  if (user && balanceInitialization.isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col gap-4 items-center">
          <Spinner size="xl" />
          <p className="text-sm text-gray-600">
            Inițializare calculatoare balance...
          </p>
          {balanceInitialization.initializationError && (
            <p className="text-sm text-red-600">
              Eroare: {balanceInitialization.initializationError}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Create router based on authentication state
  const router = createAppRouter(!!user, handleLogout);

  // ✅ CLEAN ORCHESTRATOR - React Router v7 pattern
  // RouterProvider must be at the top level for useNavigate to work
  return (
    <ToastProvider>
      <OfflineIndicator />
      <ToastContainer children={null} />
      <Toaster />
      <RouterProvider router={router} />
    </ToastProvider>
  );
};

export default App;